import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeftRight, Plus, AlertCircle, Loader2 } from "lucide-react";
import { EventoDialog, EventoCard } from "./FinanciacionContent";
import {
  getProyeccionOperaciones,
  crearProyeccionOperacion,
  actualizarProyeccionOperacion,
  eliminarProyeccionOperacion,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// W20.10: pestaña "Otras hipótesis" del menú superior de Proyección.
// Los movimientos manuales de balance (eventos, el equivalente a las "otras
// hipótesis" del Excel) viven aquí: se crean, se consultan y quedan guardados.
// El motor no cambia: son las mismas operaciones `evento_balance` de siempre
// (partida doble obligatoria, Σdebe = Σhaber), antes escondidas en Financiación.
// ---------------------------------------------------------------------------

export default function OtrasHipotesisContent({ empresaId, ejercicios, aniosProyectar, readOnly }) {
  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [eventoEditando, setEventoEditando] = useState(null);
  const [eventoBorrando, setEventoBorrando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  // Años proyectados derivados de los ejercicios con datos + horizonte global
  // (mismo cálculo que Financiación/CAPEX).
  const ultimoAnio =
    ejercicios && ejercicios.length > 0
      ? Math.max(...ejercicios.map((e) => e.anio))
      : new Date().getFullYear();
  const n = Math.max(1, aniosProyectar || 1);
  const aniosProyectados = Array.from({ length: n }, (_, i) => ultimoAnio + 1 + i);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const res = await getProyeccionOperaciones(empresaId);
      setOperaciones((res.data || []).filter((op) => op.tipo === "evento_balance"));
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar las hipótesis");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  const handleNuevo = () => {
    setEventoEditando(null);
    setDialogOpen(true);
  };

  const handleEditar = (evento) => {
    setEventoEditando(evento);
    setDialogOpen(true);
  };

  const handleGuardar = async (datos) => {
    try {
      setGuardando(true);
      if (eventoEditando) {
        await actualizarProyeccionOperacion(empresaId, eventoEditando.id, {
          nombre: datos.nombre,
          params: datos.params,
        });
        toast.success("Hipótesis actualizada");
      } else {
        await crearProyeccionOperacion(empresaId, datos);
        toast.success("Hipótesis creada");
      }
      setDialogOpen(false);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la hipótesis");
    } finally {
      setGuardando(false);
    }
  };

  const handleBorrar = async () => {
    if (!eventoBorrando) return;
    try {
      setBorrando(true);
      await eliminarProyeccionOperacion(empresaId, eventoBorrando.id);
      toast.success("Hipótesis eliminada");
      setEventoBorrando(null);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar la hipótesis");
    } finally {
      setBorrando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="otras-hipotesis-content">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Movimientos manuales del balance proyectado en un año futuro (ampliación de
          capital, mover deuda, dividendo extraordinario…). Cada hipótesis es un asiento
          de partida doble: el sistema no deja guardar si el debe y el haber no cuadran.
        </p>
        {!readOnly && (
          <Button onClick={handleNuevo} className="shrink-0" data-testid="nueva-hipotesis">
            <Plus className="h-4 w-4 mr-1" />
            Nueva hipótesis
          </Button>
        )}
      </div>

      {operaciones.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/20">
          <div className="p-4 rounded-full bg-muted mb-4">
            <ArrowLeftRight className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="font-semibold mb-1">Sin otras hipótesis</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Registra aquí los movimientos de balance que en Excel serían "otras
            hipótesis": quedan guardados y consultables, y se aplican al balance
            proyectado en su año.
          </p>
          {!readOnly && (
            <Button variant="outline" className="mt-4" onClick={handleNuevo}>
              <Plus className="h-4 w-4 mr-1" />
              Nueva hipótesis
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {operaciones.map((evento) => (
            <EventoCard
              key={evento.id}
              evento={evento}
              readOnly={readOnly}
              onEditar={handleEditar}
              onBorrar={setEventoBorrando}
            />
          ))}
        </div>
      )}

      <EventoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        evento={eventoEditando}
        aniosProyectados={aniosProyectados}
        onGuardar={handleGuardar}
        guardando={guardando}
      />

      <AlertDialog open={!!eventoBorrando} onOpenChange={(o) => !o && setEventoBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la hipótesis?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará «{eventoBorrando?.nombre}» y su efecto sobre el balance
              proyectado. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={borrando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrar();
              }}
              disabled={borrando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {borrando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
